import { useState } from 'react';
import { useLocation } from 'react-router';
import { useCurrentUser } from '../../../Session';
import { useGetPurchaseTransactionQuery, UserType } from '../../../api';
import type { QuotationUpdatedSubscription } from '../../../api';
import { LocationHistory } from '../../../components/LocationHistory';
import QuotationUpdateHandler from '../../../components/QuotationUpdateHandler';
import useDidUpdateEffect from '../../../utilities/useDidUpdateEffect';
import VehicleDiagram from '../VehicleDiagram';
import { DownloadPurchaseTransactionDetailFileProvider } from '../components';
import EditPurchaseTransaction from './EditPurchaseTransaction';
import ViewPurchaseTransaction from './ViewPurchaseTransaction';

export type PurchaseTransactionDetailProps = {
    transactionId: string;
};

const PurchaseTransactionDetail = ({ transactionId }: PurchaseTransactionDetailProps) => {
    const [onEdition, setOnEdition] = useState<boolean>(false);
    const [showVehicleDetails, setShowVehicleDetails] = useState(false);
    const [quotationUpdated, setQuotationUpdated] = useState<QuotationUpdatedSubscription['quotationUpdated']>();
    const location = useLocation();
    const currentUser = useCurrentUser();
    const displayQuotation = [UserType.Approver, UserType.ValuationTeam, UserType.Admin].includes(currentUser.type);

    const { refreshPurchaseTransactionDetail } = (location.state || {}) as LocationHistory;
    const { data, refetch } = useGetPurchaseTransactionQuery({
        fetchPolicy: 'cache-and-network',
        variables: { transactionId },
    });
    useDidUpdateEffect(() => {
        if (refreshPurchaseTransactionDetail) {
            refetch({ transactionId });
        }
    }, [refetch, transactionId, refreshPurchaseTransactionDetail]);

    const purchaseTransaction = data?.purchaseTransaction;

    if (!purchaseTransaction) {
        return null;
    }

    const quotationUpdateHandler =
        displayQuotation && !showVehicleDetails ? (
            <QuotationUpdateHandler onUpdate={setQuotationUpdated} purchaseTransactionId={transactionId} />
        ) : null;

    if (onEdition) {
        return (
            <>
                {quotationUpdateHandler}
                <EditPurchaseTransaction
                    goToView={() => setOnEdition(false)}
                    purchaseTransaction={purchaseTransaction}
                    quotationUpdated={quotationUpdated}
                    refetchPurchaseTransaction={refetch}
                />
            </>
        );
    }

    if (showVehicleDetails) {
        return (
            <VehicleDiagram
                comments={purchaseTransaction.valuation.vehicleDiagramComments}
                goToView={() => setShowVehicleDetails(false)}
                purchaseTransaction={purchaseTransaction}
            />
        );
    }

    return (
        <>
            {quotationUpdateHandler}
            <DownloadPurchaseTransactionDetailFileProvider>
                {downloadFile => (
                    <ViewPurchaseTransaction
                        downloadPurchaseTransactionDetailFile={downloadFile}
                        goToEdition={() => setOnEdition(true)}
                        goToVehicleDetails={() => setShowVehicleDetails(true)}
                        purchaseTransaction={purchaseTransaction}
                        quotationUpdated={quotationUpdated}
                        refetchPurchaseTransaction={refetch}
                    />
                )}
            </DownloadPurchaseTransactionDetailFileProvider>
        </>
    );
};

export default PurchaseTransactionDetail;
